import { IdCard, ShieldX } from "lucide-react";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { SegmentBar } from "./segment-bar";
import { T } from "./verified-id-widget.i18n";
import { WidgetCard } from "./widget-card";

export function VerifiedIdWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("verified_id", target);
  const t = useT(T);
  const { count } = useDashboardFormat();

  return (
    <WidgetCard
      icon={IdCard}
      title={t.verifiedId}
      description={t.verifiedIdDescription}
      badge={data ? t.clientCount(count(data.totalClients)) : undefined}
      footer={data ? { label: t.openClientsReview, to: data.link } : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data && (
        <>
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <ShieldX className="h-5 w-5" />
            </div>
            <div>
              <p className="text-3xl font-bold tracking-tight">{count(data.notVerified)}</p>
              <p className="text-sm text-muted-foreground">{t.notVerified}</p>
            </div>
          </div>
          <SegmentBar
            segments={[
              { key: "verified", value: data.verified, tone: "success" },
              { key: "notVerified", value: data.notVerified, tone: "destructive" },
            ]}
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-success" />
              {t.verifiedCount(count(data.verified))}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-destructive" />
              {t.notVerifiedCount(count(data.notVerified))}
            </span>
          </div>
        </>
      )}
    </WidgetCard>
  );
}
